// src/pages/CreateSurvey.jsx
import axios from "axios"
import SnackbarComponent from "components/SnackBar"
import CreatableSelect from "react-select/creatable"
import config from "config/config"
import React, { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import {
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Label,
  Input,
  Button,
  FormGroup,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormFeedback,
} from "reactstrap"

/**
 * CreateSurvey
 * - Adds a confirmation modal that requires entering a threshold (1–10) before creating a survey.
 * - The threshold is validated and included in the payload as `threshold`.
 * - If your backend uses a different field name or does not accept it, rename/remove accordingly.
 */
const CreateSurvey = () => {
  const navigate = useNavigate()
  const [err, setErr] = useState(null)
  const [success, setSuccess] = useState(null)
  const [categories, setCategories] = useState([])
  const [thresholdModalOpen, setThresholdModalOpen] = useState(false)
  const [threshold, setThreshold] = useState("")
  const [thresholdTouched, setThresholdTouched] = useState(false)
  const mode = useSelector(state => state.Layout.layoutMode)
  const leftSideBarType = useSelector(state => state.Layout.leftSideBarType)
  const sidebarWidth = leftSideBarType === "condensed" ? 85 : 260
  const activeOrganization = useSelector(
    state => state.organizationReducer.activeOrganization,
  )
  const userData = useSelector(state => state.Login.userData)

  const [survey, setSurvey] = useState({
    organizationId: activeOrganization?.id,
    title: "",
    description: "",
    questions: [],
    threshold: undefined, // set when confirmed in modal
  })

  const isValidThreshold = value => {
    const n = Number(value)
    return Number.isInteger(n) && n >= 1 && n <= 10
  }

  const fetchCategories = async () => {
    try {
      const response = await axios.get(
        `${config.backendUrl}/survey/getCategoriesByOrg/${activeOrganization.id}`,
        {
          headers: {
            Authorization: `Bearer ${userData.token}`,
          },
        },
      )
      const categoryNames = (response?.data?.categories || []).map(
        cat => cat.name,
      )
      setCategories(categoryNames)
    } catch (err) {
      console.log(err)
      setErr("Error fetching categories.")
    }
  }

  // Survey inputs
  const handleInputChange = e => {
    const { name, value } = e.target
    setSurvey(prev => ({ ...prev, [name]: value }))
  }

  const handleQuestionChange = (e, questionIndex) => {
    const { name, value } = e.target
    setSurvey(prev => {
      const updatedQuestions = [...prev.questions]
      updatedQuestions[questionIndex] = {
        ...updatedQuestions[questionIndex],
        [name]: value,
      }
      return { ...prev, questions: updatedQuestions }
    })
  }

  const addQuestion = () => {
    setSurvey(prev => ({
      ...prev,
      questions: [
        ...prev.questions,
        {
          questionText: "",
          questionType: "text",
          category: "",
          options: ["", "", "", ""],
        },
      ],
    }))
  }

  const deleteQuestion = questionIndex => {
    setSurvey(prev => ({
      ...prev,
      questions: prev.questions.filter((_, index) => index !== questionIndex),
    }))
  }

  const handleOptionChange = (e, questionIndex, optionIndex) => {
    const { value } = e.target
    setSurvey(prev => {
      const updatedQuestions = [...prev.questions]
      updatedQuestions[questionIndex].options[optionIndex] = value
      return { ...prev, questions: updatedQuestions }
    })
  }

  // Open threshold modal instead of immediate submit
  const openThresholdModal = () => {
    setThresholdModalOpen(true)
  }

  const closeThresholdModal = () => {
    setThresholdModalOpen(false)
    setThreshold("")
    setThresholdTouched(false)
  }

  const confirmAndCreate = async () => {
    setThresholdTouched(true)
    if (!isValidThreshold(threshold)) return

    try {
      const payload = {
        ...survey,
        organizationId: activeOrganization.id, // ensure fresh org id
        threshold: Number(threshold),
      }

      const response = await axios.post(
        `${config.backendUrl}/survey/create`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${userData.token}`,
          },
        },
      )

      setSuccess("Survey Created Successfully!")
      setErr(null)
      setSurvey({
        organizationId: activeOrganization.id,
        title: "",
        description: "",
        questions: [],
        threshold: undefined,
      })
      closeThresholdModal()
    } catch (err) {
      console.log(err)
      setErr("Error Creating Survey!")
      setSuccess(null)
    }
  }

  useEffect(() => {
    if (activeOrganization?.id && userData?.token) {
      fetchCategories()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeOrganization?.id, userData?.token])

  const thresholdInvalid = thresholdTouched && !isValidThreshold(threshold)

  return (
    <React.Fragment>
      <SnackbarComponent
        err={err}
        success={success}
        setErr={setErr}
        setSuccess={setSuccess}
      />

      {/* Threshold Modal */}
      <Modal isOpen={thresholdModalOpen} toggle={closeThresholdModal} centered backdrop="static">
        <div style={{ backgroundColor: mode === "dark" ? "#1f2937" : "#fff", borderRadius: "16px", overflow: "hidden", fontFamily: "Inter, sans-serif" }}>
          {/* Header */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "20px 24px 16px", borderBottom: `1px solid ${mode === "dark" ? "#374151" : "#e5e7eb"}` }}>
            <div>
              <h5 style={{ margin: 0, fontWeight: 700, fontSize: "17px", color: mode === "dark" ? "#fff" : "#111827" }}>
                Set Survey Threshold
              </h5>
              <p style={{ margin: 0, fontSize: "13px", color: mode === "dark" ? "#9ca3af" : "#6b7280", marginTop: "2px" }}>
                Minimum score (1–10) to trigger a follow-up action
              </p>
            </div>
            <button onClick={closeThresholdModal} style={{ background: "transparent", border: "none", cursor: "pointer", color: mode === "dark" ? "#9ca3af" : "#6b7280", fontSize: "20px", lineHeight: 1, padding: "4px" }}>
              ×
            </button>
          </div>

          {/* Body */}
          <div style={{ padding: "24px" }}>
            <label style={{ display: "block", fontSize: "12px", fontWeight: 600, textTransform: "uppercase", color: mode === "dark" ? "#9ca3af" : "#6b7280", marginBottom: "6px", letterSpacing: "0.05em" }}>
              Threshold
            </label>
            <input
              type="number"
              min={1}
              max={10}
              step={1}
              value={threshold}
              onChange={e => setThreshold(e.target.value)}
              onBlur={() => setThresholdTouched(true)}
              placeholder="Enter a number between 1 and 10"
              style={{
                width: "100%",
                padding: "10px 14px",
                borderRadius: "8px",
                border: `1px solid ${thresholdInvalid ? "#ef4444" : mode === "dark" ? "#374151" : "#e5e7eb"}`,
                backgroundColor: mode === "dark" ? "#374151" : "#f9fafb",
                color: mode === "dark" ? "#fff" : "#111827",
                fontSize: "14px",
                fontFamily: "Inter, sans-serif",
                outline: "none",
              }}
            />
            {thresholdInvalid && (
              <p style={{ color: "#ef4444", fontSize: "13px", marginTop: "6px", marginBottom: 0 }}>
                Please enter a whole number between 1 and 10.
              </p>
            )}
            <p style={{ color: mode === "dark" ? "#9ca3af" : "#6b7280", fontSize: "12px", marginTop: "10px", marginBottom: 0 }}>
              Customers who score below this threshold will be flagged for follow-up.
            </p>
          </div>

          {/* Footer */}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", padding: "16px 24px 20px", borderTop: `1px solid ${mode === "dark" ? "#374151" : "#e5e7eb"}` }}>
            <button onClick={closeThresholdModal} style={{ padding: "10px 20px", borderRadius: "8px", border: `1px solid ${mode === "dark" ? "#374151" : "#e5e7eb"}`, backgroundColor: "transparent", color: mode === "dark" ? "#9ca3af" : "#6b7280", fontSize: "14px", fontWeight: 500, cursor: "pointer", fontFamily: "Inter, sans-serif" }}>
              Cancel
            </button>
            <button onClick={confirmAndCreate} disabled={!isValidThreshold(threshold)} style={{ padding: "10px 24px", borderRadius: "8px", border: "none", backgroundColor: isValidThreshold(threshold) ? "#2990DF" : "#93c5fd", color: "#fff", fontSize: "14px", fontWeight: 600, cursor: isValidThreshold(threshold) ? "pointer" : "not-allowed", fontFamily: "Inter, sans-serif" }}>
              Confirm & Create
            </button>
          </div>
        </div>
      </Modal>

      <Row className="justify-content-center">
        <Col xl={12}>
          <Card
            className="shadow-sm br"
            style={{
              backgroundColor: mode === "dark" ? "#252F40" : "#fff",
            }}
          >
            <CardBody>
              <Row className="align-items-center mb-3">
                <Col>
                  <CardTitle
                    className="h4 mb-0 text-uppercase "
                    style={{
                      color: mode === "dark" ? "#FFFFFF" : "#000000",
                    }}
                  >
                    Create Survey
                  </CardTitle>
                </Col>
                <Col className="d-flex justify-content-end">
                  <Button
                    color="danger"
                    size="md"
                    className="mr-2"
                    onClick={() =>
                      setSurvey({
                        organizationId: activeOrganization.id,
                        title: "",
                        description: "",
                        questions: [],
                        threshold: undefined,
                      })
                    }
                  >
                    Reset
                  </Button>
                </Col>
              </Row>

              {/* Survey Title & Description */}
              <Row className="mb-3">
                <Col md={6}>
                  <FormGroup>
                    <Label>Survey Title</Label>
                    <Input
                      type="text"
                      name="title"
                      placeholder="Enter Survey Title"
                      value={survey.title}
                      onChange={handleInputChange}
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label>Survey Description</Label>
                    <Input
                      type="text"
                      name="description"
                      placeholder="Enter Survey Description"
                      value={survey.description}
                      onChange={handleInputChange}
                    />
                  </FormGroup>
                </Col>
              </Row>

              {/* Questions Section */}
              <Row>
                <CardTitle
                  className="h4 my-4 text-uppercase "
                  style={{
                    color: mode === "dark" ? "#FFFFFF" : "#000000",
                  }}
                >
                  Questions
                </CardTitle>
              </Row>

              {survey.questions.map((question, questionIndex) => (
                <Row className="mb-3 border rounded p-3" key={questionIndex}>
                  <Col md={5}>
                    <Label>Question</Label>
                    <Input
                      type="text"
                      name="questionText"
                      placeholder="Enter question text"
                      value={question.questionText}
                      onChange={e => handleQuestionChange(e, questionIndex)}
                    />
                  </Col>
                  <Col md={3}>
                    <Label>Type</Label>
                    <Input
                      type="select"
                      name="questionType"
                      value={question.questionType}
                      onChange={e => handleQuestionChange(e, questionIndex)}
                    >
                      <option value="text">Text</option>
                      <option value="rating">Rating</option>
                      <option value="multiple choice">Multiple Choice</option>
                    </Input>
                  </Col>
                  <Col md={3}>
                    <Label>Label</Label>
                    <CreatableSelect
                      isClearable
                      options={categories.map(cat => ({
                        label: cat,
                        value: cat,
                      }))}
                      value={
                        question.category
                          ? {
                              label: question.category,
                              value: question.category,
                            }
                          : null
                      }
                      onChange={selectedOption => {
                        const selectedCategory = selectedOption
                          ? selectedOption.value
                          : ""
                        handleQuestionChange(
                          {
                            target: {
                              name: "category",
                              value: selectedCategory,
                            },
                          },
                          questionIndex,
                        )
                      }}
                      onCreateOption={newCategory => {
                        setCategories(prev => [...prev, newCategory])
                        handleQuestionChange(
                          { target: { name: "category", value: newCategory } },
                          questionIndex,
                        )
                      }}
                      placeholder="Select or create category"
                    />
                  </Col>

                  <Col md={1} className="d-flex align-items-end">
                    <Button
                      color="danger"
                      size="sm"
                      className="mt-3"
                      onClick={() => deleteQuestion(questionIndex)}
                    >
                      Delete
                    </Button>
                  </Col>

                  {/* Multiple Choice Options */}
                  {question.questionType === "multiple choice" && (
                    <Col md={12} className="mt-2">
                      <Label>Options</Label>
                      <Row>
                        {question.options.map((option, optionIndex) => (
                          <Col md={3} key={optionIndex}>
                            <Input
                              type="text"
                              placeholder={`Option ${optionIndex + 1}`}
                              value={option}
                              onChange={e =>
                                handleOptionChange(
                                  e,
                                  questionIndex,
                                  optionIndex,
                                )
                              }
                            />
                          </Col>
                        ))}
                      </Row>
                    </Col>
                  )}
                </Row>
              ))}

              <div style={{ height: "80px" }} />
            </CardBody>
          </Card>
        </Col>
      </Row>
      {/* Floating action bar */}
      <div
        style={{
          position: "fixed",
          bottom: "32px",
          left: `calc(${sidebarWidth}px + (100vw - ${sidebarWidth}px) / 2)`,
          transform: "translateX(-50%)",
          zIndex: 1050,
          backgroundColor: mode === "dark" ? "#1a2332" : "#fff",
          border: `1px solid ${mode === "dark" ? "#3a4556" : "#e5e7eb"}`,
          borderRadius: "14px",
          boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
          padding: "14px 24px",
          display: "flex",
          alignItems: "center",
          gap: "16px",
          minWidth: "340px",
        }}
      >
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: "14px", color: mode === "dark" ? "#fff" : "#111827" }}>
            {survey.questions.length} question{survey.questions.length !== 1 ? "s" : ""} added
          </div>
          <div style={{ fontSize: "12px", color: mode === "dark" ? "#9ca3af" : "#6b7280" }}>
            {survey.title || "Untitled survey"}
          </div>
        </div>

        <button
          onClick={addQuestion}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            padding: "10px 18px",
            borderRadius: "8px",
            border: `1px solid #2990DF`,
            backgroundColor: "transparent",
            color: "#2990DF",
            fontSize: "14px",
            fontWeight: 600,
            cursor: "pointer",
            fontFamily: "Inter, sans-serif",
            whiteSpace: "nowrap",
          }}
        >
          + Add Question
        </button>

        <button
          onClick={openThresholdModal}
          style={{
            padding: "10px 22px",
            borderRadius: "8px",
            border: "none",
            backgroundColor: "#2990DF",
            color: "#fff",
            fontSize: "14px",
            fontWeight: 600,
            cursor: "pointer",
            fontFamily: "Inter, sans-serif",
            whiteSpace: "nowrap",
          }}
        >
          Submit Survey
        </button>
      </div>
    </React.Fragment>
  )
}

export default CreateSurvey
